import React, { useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useTranslation } from "react-i18next";
import { Button, Box, Chip, Checkbox, Paper, TextField } from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import moment from "moment";
import { selectTours, getTours } from "redux/slices/tourSlice";
import { Autocomplete } from "@material-ui/lab";
import { MuiPickersUtilsProvider } from "@material-ui/pickers";
import MomentUtils from "@date-io/moment";
import { DatePicker } from "components/Shared/mui-formik-inputs";
import { sortOrder as deliveryTimings } from "util/customers/deliveryTimings";

const useStyles = makeStyles((theme) => ({
  container: {
    display: "flex",
    alignItems: "center",
    flexWrap: "wrap",
    gap: "12px",
    marginBottom: "8px",
    "& svg": {
      color: "white !important",
    },
    "& label": {
      color: "white !important",
    },
    "& .MuiOutlinedInput-notchedOutline": {
      borderColor: "white !important",
    },
    "& .MuiOutlinedInput-root": {
      color: "white !important",
      borderRadius: "4px",
    },
    "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": {
      borderColor: "white !important",
    },
    "& .MuiInputLabel-outlined": {
      color: "white !important",
    },
    "& .MuiIconButton-root": {
      color: "white !important",
    },
  },
  dateBox: {
    width: "190px",
    "& .MuiFormControl-marginDense": {
      marginTop: 0,
      marginBottom: 0,
    },
  },
  tourBox: {
    minWidth: "220px",
    flex: "1 1 240px",
    maxWidth: "380px",
    "& .MuiFormControl-marginDense": {
      marginTop: 0,
      marginBottom: 0,
    },
  },
  departureBox: {
    minWidth: "180px",
    flex: "1 1 200px",
    maxWidth: "300px",
    "& .MuiFormControl-marginDense": {
      marginTop: 0,
      marginBottom: 0,
    },
  },
  button: {
    color: "white",
    fontWeight: "bold",
    whiteSpace: "nowrap",
  },
}));

const RouteFilters = ({ value, onChange }) => {
  const { t, i18n } = useTranslation();
  const classes = useStyles();
  const dispatch = useDispatch();
  const tours = useSelector(selectTours);
  const adapterLocale = (i18n?.language || "en").toLowerCase().startsWith("de") ? "de" : "en";

  useEffect(() => {
    dispatch(getTours());
  }, [dispatch]);

  const filters = value || {
    dateRange: {
      startDate: null,
      endDate: null
    },
    tourIds: [],
    departures: []
  };

  const handleFilterUpdate = (newFilters) => {
    if (onChange) {
      onChange(newFilters);
    }
  };

  const resetFilters = () => {
    const clearedFilters = {
      dateRange: {
        startDate: null,
        endDate: null
      },
      tourIds: [],
      departures: []
    };
    handleFilterUpdate(clearedFilters);
  };

  const handleStartDateChange = (date) => {
    const newFilters = {
      ...filters,
      dateRange: {
        ...filters.dateRange,
        startDate: date && date.isValid() ? date : null
      }
    };
    handleFilterUpdate(newFilters);
  };

  const handleEndDateChange = (date) => {
    const newFilters = {
      ...filters,
      dateRange: {
        ...filters.dateRange,
        endDate: date && date.isValid() ? date : null
      }
    };
    handleFilterUpdate(newFilters);
  };

  const departureOptions = React.useMemo(() => {
    const targetTours =
      filters.tourIds && filters.tourIds.length > 0
        ? tours.filter((tour) => filters.tourIds.includes(tour.id))
        : tours;

    const timings = new Set();
    targetTours.forEach((tour) => {
      (tour.delivery_timing_configs || []).forEach((config) => {
        if (config.delivery_timing) {
          timings.add(config.delivery_timing);
        }
      });
    });

    const normalizeTiming = (str) =>
      str ? str.toUpperCase().replace(/[\\s_]+/g, " ") : "";

    return [...timings].sort((a, b) => {
      const idxA = deliveryTimings.indexOf(normalizeTiming(a));
      const idxB = deliveryTimings.indexOf(normalizeTiming(b));
      if (idxA !== -1 && idxB !== -1) return idxA - idxB;
      if (idxA !== -1) return -1;
      if (idxB !== -1) return 1;
      return a.localeCompare(b);
    });
  }, [tours, filters.tourIds]);

  const handleTourChange = (event, newValue) => {
    const newTourIds = newValue.map((tour) => tour.id);
    const targetTours =
      newTourIds.length > 0
        ? tours.filter((tour) => newTourIds.includes(tour.id))
        : tours;

    const allowedTimings = new Set();
    targetTours.forEach((tour) => {
      (tour.delivery_timing_configs || []).forEach((config) => {
        if (config.delivery_timing) {
          allowedTimings.add(config.delivery_timing);
        }
      });
    });

    const newDepartures = (filters.departures || []).filter((dep) =>
      allowedTimings.has(dep)
    );

    const newFilters = {
      ...filters,
      tourIds: newTourIds,
      departures: newDepartures,
    };
    handleFilterUpdate(newFilters);
  };

  const handleDepartureChange = (event, newValue) => {
    const newFilters = {
      ...filters,
      departures: newValue
    };
    handleFilterUpdate(newFilters);
  };

  return (
    <MuiPickersUtilsProvider
      utils={MomentUtils}
      locale={adapterLocale}
      libInstance={moment}
    >
      <Box className={classes.container}>
        <Box className={classes.dateBox}>
          <DatePicker
            label={t("From Date")}
            onChange={handleStartDateChange}
            name="start_date"
            value={filters.dateRange.startDate}
            errors={{}}
            clearable
            inputVariant="outlined"
            disableFuture
            maxDate={moment().subtract(5, "days").endOf("day")}
          />
        </Box>
        <Box className={classes.dateBox}>
          <DatePicker
            label={t("To Date")}
            onChange={handleEndDateChange}
            name="end_date"
            value={filters.dateRange.endDate}
            errors={{}}
            clearable
            inputVariant="outlined"
            disableFuture
            maxDate={moment().subtract(5, "days").endOf("day")}
            minDate={filters.dateRange.startDate || undefined}
          />
        </Box>
        <Box className={classes.tourBox}>
          <Autocomplete
            multiple
            size="small"
            options={tours}
            getOptionLabel={(option) => option.name}
            value={tours.filter(tour => filters.tourIds.includes(tour.id))}
            onChange={handleTourChange}
            renderInput={(params) => (
              <TextField
                {...params}
                label={t("Tour")}
                variant="outlined"
                margin="dense"
                size="small"
              />
            )}
            PaperComponent={({ children, ...other }) => (
              <Paper {...other} style={{ backgroundColor: "#333" }}>
                {children}
              </Paper>
            )}
            renderOption={(option, { selected }) => (
              <div style={{ display: "flex", alignItems: "center", color: "white", backgroundColor: "#333" }}>
                <Checkbox
                  style={{ color: "white" }}
                  checked={selected}
                />
                <span style={{ color: "white" }}>{option.name}</span>
              </div>
            )}
            renderTags={(value, getTagProps) =>
              value.map((option, index) => (
                <Chip
                  variant="outlined"
                  label={option.name}
                  size="small"
                  style={{ color: "white", borderColor: "white" }}
                  {...getTagProps({ index })}
                />
              ))
            }
          />
        </Box>
        <Box className={classes.departureBox}>
          <Autocomplete
            multiple
            size="small"
            options={departureOptions}
            getOptionLabel={(option) => t(option)}
            value={(filters.departures || []).filter((dep) => departureOptions.includes(dep))}
            onChange={handleDepartureChange}
            renderInput={(params) => (
              <TextField
                {...params}
                label={t("Departure")}
                variant="outlined"
                margin="dense"
                size="small"
              />
            )}
            PaperComponent={({ children, ...other }) => (
              <Paper {...other} style={{ backgroundColor: "#333" }}>
                {children}
              </Paper>
            )}
            renderOption={(option, { selected }) => (
              <div style={{ display: "flex", alignItems: "center", color: "white", backgroundColor: "#333" }}>
                <Checkbox
                  style={{ color: "white" }}
                  checked={selected}
                />
                <span style={{ color: "white" }}>{t(option)}</span>
              </div>
            )}
            renderTags={(value, getTagProps) =>
              value.map((option, index) => (
                <Chip
                  variant="outlined"
                  label={t(option)}
                  size="small"
                  style={{ color: "white", borderColor: "white" }}
                  {...getTagProps({ index })}
                />
              ))
            }
          />
        </Box>
        <Box ml={1}>
          <Button
            size="small"
            className={classes.button}
            onClick={resetFilters}
          >
            {t("Reset Filters")}
          </Button>
        </Box>
      </Box>
    </MuiPickersUtilsProvider>
  );
};

export default RouteFilters;
